// ─────────────────────────────────────────────────────────────────────────
// industryLexicon - per-industry vocabulary for the route ("plan your day")
// surface. The demo Territory tool is hardcoded to beverage nouns
// ("accounts / OOS / cases"). Every Wedge user is in a different vertical,
// so the route chrome speaks THEIR industry: a med-device rep sees
// "clinics", a solar rep sees "doors", a marine rep sees "dealers".
//
// Split of responsibility (mirrors how the Wedge already handles voice):
//   - THIS module = the fast, deterministic, offline UI-chrome nouns
//     (what is a "stop", what is the "day/run"). Static map, no network.
//   - dayBriefingPrompt.js = the long-form prose, written by the
//     industry-expert agent in the rep's voice. That path scales to all 73
//     industries with no hardcoding; this map just covers the known
//     verticals with a graceful neutral fallback for the rest.
//
// Keyed off wedge_prefs.industry.primary, read exactly the way
// WedgeBundle's VERTICAL_VOCAB reads it (rv_wedge_prefs_cache), so a user's
// route surface and their Today surface always agree on their vertical.
//
// HONEST-LABELING RULE (boardroom ship gate): the free surface says
// "suggested order" / "plan your day", NEVER "optimized", never an ETA or a
// minutes-saved number. Those words are reserved for the paid Routes path.
// The labels below deliberately contain none of them.
// ─────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} RouteLexicon
 * @property {string} stopSingular   one place the rep visits ("clinic", "door", "account")
 * @property {string} stopPlural     plural of stopSingular
 * @property {string} sequenceNoun   the ordered day of stops ("run", "route", "loop", "day")
 * @property {string} planVerb       imperative for the CTA ("Plan", "Line up", "Map out")
 */

// Neutral professional default. NOT beverage. Any industry we do not have a
// bespoke entry for degrades to this instead of leaking demo vocabulary.
const DEFAULT_LEXICON = Object.freeze({
  stopSingular: 'stop',
  stopPlural: 'stops',
  sequenceNoun: 'day',
  planVerb: 'Plan',
});

// Bespoke entries. Keys match WedgeBundle VERTICAL_VOCAB / wedge_prefs
// industry.primary. Add a key here to give a vertical bespoke chrome; leave
// it out and it inherits DEFAULT_LEXICON (still correct, just generic).
const LEXICON = Object.freeze({
  beverage:          { stopSingular: 'account',   stopPlural: 'accounts',   sequenceNoun: 'route', planVerb: 'Plan' },
  foodservice:       { stopSingular: 'operator',  stopPlural: 'operators',  sequenceNoun: 'route', planVerb: 'Plan' },
  medical:           { stopSingular: 'clinic',    stopPlural: 'clinics',    sequenceNoun: 'day',   planVerb: 'Plan' },
  pharma:            { stopSingular: 'office',     stopPlural: 'offices',    sequenceNoun: 'day',   planVerb: 'Plan' },
  dental:            { stopSingular: 'practice',   stopPlural: 'practices',  sequenceNoun: 'day',   planVerb: 'Plan' },
  merchant_services: { stopSingular: 'merchant',   stopPlural: 'merchants',  sequenceNoun: 'route', planVerb: 'Line up' },
  tech:              { stopSingular: 'account',    stopPlural: 'accounts',   sequenceNoun: 'day',   planVerb: 'Plan' },
  realestate:        { stopSingular: 'listing',    stopPlural: 'listings',   sequenceNoun: 'day',   planVerb: 'Plan' },
  construction:      { stopSingular: 'jobsite',    stopPlural: 'jobsites',   sequenceNoun: 'route', planVerb: 'Line up' },
  industrial:        { stopSingular: 'account',    stopPlural: 'accounts',   sequenceNoun: 'route', planVerb: 'Plan' },
  insurance:         { stopSingular: 'prospect',   stopPlural: 'prospects',  sequenceNoun: 'day',   planVerb: 'Plan' },
  automotive:        { stopSingular: 'dealer',     stopPlural: 'dealers',    sequenceNoun: 'route', planVerb: 'Plan' },
  logistics:         { stopSingular: 'shipper',    stopPlural: 'shippers',   sequenceNoun: 'route', planVerb: 'Line up' },
  home_services:     { stopSingular: 'home',       stopPlural: 'homes',      sequenceNoun: 'route', planVerb: 'Line up' },
  defense:           { stopSingular: 'command',    stopPlural: 'commands',   sequenceNoun: 'day',   planVerb: 'Plan' },
  energy:            { stopSingular: 'site',       stopPlural: 'sites',      sequenceNoun: 'route', planVerb: 'Plan' },
  promo:             { stopSingular: 'account',    stopPlural: 'accounts',   sequenceNoun: 'day',   planVerb: 'Plan' },
  cannabis:          { stopSingular: 'dispensary', stopPlural: 'dispensaries', sequenceNoun: 'route', planVerb: 'Plan' },
  petspecialty:      { stopSingular: 'shop',       stopPlural: 'shops',      sequenceNoun: 'route', planVerb: 'Plan' },
  sportsoutdoor:     { stopSingular: 'retailer',   stopPlural: 'retailers',  sequenceNoun: 'route', planVerb: 'Plan' },
  musicvenues:       { stopSingular: 'venue',      stopPlural: 'venues',     sequenceNoun: 'day',   planVerb: 'Plan' },
  telecom:           { stopSingular: 'account',    stopPlural: 'accounts',   sequenceNoun: 'route', planVerb: 'Plan' },
  hvacplumbing:      { stopSingular: 'wholesaler', stopPlural: 'wholesalers', sequenceNoun: 'route', planVerb: 'Line up' },
  finwholesale:      { stopSingular: 'office',     stopPlural: 'offices',    sequenceNoun: 'day',   planVerb: 'Plan' },
  agtech:            { stopSingular: 'grower',     stopPlural: 'growers',    sequenceNoun: 'route', planVerb: 'Plan' },
  k12edtech:         { stopSingular: 'district',   stopPlural: 'districts',  sequenceNoun: 'day',   planVerb: 'Plan' },
  coffeebiz:         { stopSingular: 'cafe',       stopPlural: 'cafes',      sequenceNoun: 'route', planVerb: 'Plan' },
  hospitalitytech:   { stopSingular: 'property',   stopPlural: 'properties', sequenceNoun: 'day',   planVerb: 'Plan' },
  marinepower:       { stopSingular: 'dealer',     stopPlural: 'dealers',    sequenceNoun: 'route', planVerb: 'Plan' },
  jansan:            { stopSingular: 'account',    stopPlural: 'accounts',   sequenceNoun: 'route', planVerb: 'Line up' },
  probeauty:         { stopSingular: 'salon',      stopPlural: 'salons',     sequenceNoun: 'route', planVerb: 'Plan' },
  agencypitch:       { stopSingular: 'brand',      stopPlural: 'brands',     sequenceNoun: 'day',   planVerb: 'Plan' },
});

/**
 * Read the rep's primary industry the same way WedgeBundle's VERTICAL_VOCAB
 * does, so route + Today never disagree. Best-effort; returns "" when prefs
 * are unavailable (SSR, private mode, first run).
 * @returns {string} lowercased industry.primary, or ""
 */
export function readIndustryPrimary() {
  try {
    if (typeof localStorage === 'undefined') return '';
    const raw = localStorage.getItem('rv_wedge_prefs_cache');
    if (!raw) return '';
    const p = JSON.parse(raw);
    return String((p && p.industry && p.industry.primary) || '').trim().toLowerCase();
  } catch (e) {
    return '';
  }
}

/**
 * Resolve the route lexicon for an industry key. Unknown / empty keys fall
 * back to the neutral professional default (never beverage). Frozen objects
 * are returned so callers cannot mutate the shared map.
 * @param {string} [industryPrimary] - wedge_prefs.industry.primary; if omitted, read from prefs cache
 * @returns {RouteLexicon}
 */
export function routeLexicon(industryPrimary) {
  const key = String(industryPrimary != null ? industryPrimary : readIndustryPrimary())
    .trim()
    .toLowerCase();
  const bespoke = LEXICON[key];
  return bespoke ? Object.freeze({ ...DEFAULT_LEXICON, ...bespoke }) : DEFAULT_LEXICON;
}

/**
 * The honest, industry-flavored labels for the route surface. Deliberately
 * free of "optimized", ETAs, and minutes-saved figures (boardroom ship
 * gate). Capitalizes the plural for headline use.
 * @param {string} [industryPrimary]
 * @returns {{ title: string, subtitle: string, cta: string, stopPlural: string, stopSingular: string }}
 */
export function routeLabels(industryPrimary) {
  const lex = routeLexicon(industryPrimary);
  const Plural = lex.stopPlural.charAt(0).toUpperCase() + lex.stopPlural.slice(1);
  return {
    // e.g. "Today's route" / "Today's day" -> normalize to a clean headline
    title: lex.sequenceNoun === 'day' ? "Today's plan" : `Today's ${lex.sequenceNoun}`,
    subtitle: `Suggested order for today's ${lex.stopPlural}`,
    cta: `${lex.planVerb} my ${lex.sequenceNoun}`,
    stopPlural: Plural,
    stopSingular: lex.stopSingular,
  };
}

export const __DEFAULT_LEXICON = DEFAULT_LEXICON;
export const __LEXICON_KEYS = Object.keys(LEXICON);
